import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import AppSnackbar from "../components/AppSnackbar";
import { BLOG_FALLBACK_IMAGE } from "../components/BlogPostCard";
import { API_BASE } from "../lib/api";
import { getAccessToken } from "../lib/auth";
import { BLOG_CATEGORY_GROUPS } from "../utils/blogTaxonomy";
import {
  IMAGE_UPLOAD_SIZE_ERROR,
  imageExceedsUploadLimit,
} from "../utils/imageUpload";
import { authFieldSx } from "./authFieldStyles";

const panelSx = {
  p: { xs: 2.5, md: 3 },
  bgcolor: "#fff",
  borderRadius: 2,
};

const yellowButtonSx = {
  "&&": {
    bgcolor: "#f7b900",
    backgroundImage: "none",
    color: "#312600",
    borderColor: "#f7b900",
    boxShadow: "none",
    fontWeight: 600,
    textTransform: "none",
  },
  "&&:hover, &&:focus-visible": {
    bgcolor: "#dca600",
    backgroundImage: "none",
    color: "#312600",
    borderColor: "#dca600",
    boxShadow: "none",
  },
};

const tagAutocompletePaperProps = {
  sx: {
    bgcolor: "#fff",
    border: "1px solid #e8e8e8",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
    "& .MuiAutocomplete-listbox": { bgcolor: "#fff" },
    "& .MuiAutocomplete-option": {
      backgroundColor: "#fff !important",
      "&:hover, &.Mui-focused": {
        backgroundColor: "#fffaf0 !important",
      },
      '&[aria-selected="true"]': {
        backgroundColor: "#fff7d6 !important",
      },
      '&[aria-selected="true"].Mui-focused, &[aria-selected="true"]:hover': {
        backgroundColor: "#ffefb0 !important",
      },
    },
  },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: "#fff",
      border: "1px solid #e8e8e8",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
      "& .MuiMenuItem-root": {
        backgroundColor: "#fff !important",
        "&:hover, &.Mui-focusVisible": {
          backgroundColor: "#fffaf0 !important",
        },
        "&.Mui-selected": {
          backgroundColor: "#fff7d6 !important",
        },
        "&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
          backgroundColor: "#ffefb0 !important",
        },
      },
    },
  },
};

export default function BlogPostCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editSlug = searchParams.get("edit");
  const isEditMode = Boolean(editSlug);
  const token = getAccessToken();

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState([]);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const localImagePreview = useMemo(
    () => (featuredImage ? URL.createObjectURL(featuredImage) : null),
    [featuredImage],
  );

  useEffect(
    () => () => {
      if (localImagePreview) URL.revokeObjectURL(localImagePreview);
    },
    [localImagePreview],
  );

  useEffect(() => {
    let active = true;

    const loadForm = async () => {
      try {
        const [categoryResponse, tagResponse] = await Promise.all([
          fetch(`${API_BASE}/blog/categories/`),
          fetch(`${API_BASE}/blog/tags/`),
        ]);
        const [categoryData, tagData] = await Promise.all([
          categoryResponse.json(),
          tagResponse.json(),
        ]);
        if (!active) return;

        const realCategories = categoryData.filter(
          (item) => item.id !== "All" && item.name !== "All",
        );
        setCategories(realCategories);
        setAllTags(tagData);

        if (!isEditMode) return;
        if (!token) throw new Error("Please log in to edit this post.");

        setLoading(true);
        const postResponse = await fetch(
          `${API_BASE}/blog/posts/${encodeURIComponent(editSlug)}/`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!postResponse.ok) {
          const data = await postResponse.json().catch(() => ({}));
          throw new Error(data.detail || "Failed to load the post.");
        }

        const post = await postResponse.json();
        if (!active) return;
        setTitle(post.title || "");
        setExcerpt(post.excerpt || "");
        setContent(post.content || "");
        setCategory(
          typeof post.category === "number"
            ? post.category
            : realCategories.find((item) => item.name === post.category)?.id ||
                "",
        );
        setTags(
          Array.isArray(post.tags)
            ? post.tags.map(
                (name) =>
                  tagData.find((item) => item.name === name) || {
                    id: null,
                    name,
                  },
              )
            : [],
        );
        setExistingImageUrl(post.image || null);
      } catch (loadError) {
        if (active) {
          setSnackbar({
            open: true,
            message: loadError.message,
            severity: "error",
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadForm();
    return () => {
      active = false;
    };
  }, [editSlug, isEditMode, token]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (imageExceedsUploadLimit(file)) {
      event.target.value = "";
      setSnackbar({
        open: true,
        message: IMAGE_UPLOAD_SIZE_ERROR,
        severity: "error",
      });
      return;
    }
    setFeaturedImage(file);
  };

  const handleSubmit = async (status) => {
    if (!token) {
      setSnackbar({
        open: true,
        message: "Please log in before publishing.",
        severity: "error",
      });
      return;
    }
    if (!title.trim()) {
      setSnackbar({
        open: true,
        message: "The title is mandatory.",
        severity: "error",
      });
      return;
    }
    if (!content || content === "<p><br></p>") {
      setSnackbar({
        open: true,
        message: "The main content cannot be empty.",
        severity: "error",
      });
      return;
    }
    if (!category) {
      setSnackbar({
        open: true,
        message: "Please select a category.",
        severity: "error",
      });
      return;
    }

    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("excerpt", excerpt.trim());
    formData.append("content", content);
    formData.append("status", status);
    formData.append("category", category);
    tags
      .filter((tag) => tag.id)
      .forEach((tag) => formData.append("tags", tag.id));
    tags
      .filter((tag) => !tag.id)
      .forEach((tag) => formData.append("new_tags", tag.name));
    if (featuredImage) {
      formData.append("featured_image", featuredImage);
    }

    setLoading(true);
    try {
      const response = await fetch(
        isEditMode
          ? `${API_BASE}/blog/posts/${encodeURIComponent(editSlug)}/edit/`
          : `${API_BASE}/blog/posts/`,
        {
          method: isEditMode ? "PUT" : "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.detail ||
            data.error ||
            data.featured_image?.[0] ||
            data.title?.[0] ||
            data.category?.[0] ||
            "The post could not be saved.",
        );
      }

      const successMessage = isEditMode
        ? "The post was updated."
        : "The post was published.";
      setSnackbar({
        open: true,
        message: successMessage,
        severity: "success",
      });
      setTimeout(() => navigate("/blog"), 1400);
    } catch (submitError) {
      setSnackbar({
        open: true,
        message: submitError.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const groupedCategories = BLOG_CATEGORY_GROUPS.map((group) => ({
    ...group,
    items: group.categories
      .map((name) => categories.find((item) => item.name === name))
      .filter(Boolean),
  })).filter((group) => group.items.length);

  const ungroupedCategories = categories.filter(
    (item) =>
      !BLOG_CATEGORY_GROUPS.some((group) =>
        group.categories.includes(item.name),
      ),
  );

  return (
    <Box sx={{ bgcolor: "#fcfcfc", minHeight: "100%" }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5 } }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: 3 }}
        >
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/blog")}
            color="inherit"
            sx={{ minWidth: 0, px: 0.5, textTransform: "none" }}
          >
            Back to posts
          </Button>
          <Typography
            component="h1"
            variant="h3"
            sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 700 }}
          >
            {isEditMode ? "Edit Post" : "Create New Post"}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2.25fr) 1fr" },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          <Paper variant="outlined" sx={panelSx}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 3 }}>
              Content Editor
            </Typography>
            <Stack spacing={2.75}>
              <TextField
                id="post-title"
                label="Post Title"
                required
                fullWidth
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                sx={{
    ...authFieldSx,
    "& .MuiInputLabel-root": {
      color: "#000",
    },
  }}
              />
              <TextField
                id="post-excerpt"
                label="Short Excerpt"
                fullWidth
                multiline
                minRows={3}
                value={excerpt}
                onChange={(event) => setExcerpt(event.target.value)}
                helperText="Short summary displayed on the card."
                sx={{
    ...authFieldSx,
    "& .MuiInputLabel-root": {
      color: "#000",
    },
  }}
              />
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
                  Main Content
                </Typography>
                <Box
                  sx={{
                    bgcolor: "#fff",
                    "& .ql-container": {
                      borderColor: "#f7b900",
                      height: { xs: 360, md: 500 },
                      borderBottomLeftRadius: 8,
                      borderBottomRightRadius: 8,
                      fontFamily: '"Poppins", sans-serif',
                      fontSize: 14,
                    },
                    "& .ql-editor": {
                      height: "100%",
                      overflowY: "auto",
                      overflowX: "hidden",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      WebkitOverflowScrolling: "touch",
                      "& img, & iframe, & video": {
                        maxWidth: "100%",
                        height: "auto",
                      },
                    },
                    "& .ql-toolbar": {
  borderColor: "#f7b900",
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,

  "& button:hover .ql-stroke, & button.ql-active .ql-stroke": {
    stroke: "#f7b900",
  },

  "& button:hover .ql-fill, & button.ql-active .ql-fill": {
    fill: "#f7b900",
  },

  "& .ql-picker-label:hover, & .ql-picker-label.ql-active": {
    color: "#f7b900",
  },

  "& .ql-picker-label:hover .ql-stroke, & .ql-picker-label.ql-active .ql-stroke": {
    stroke: "#f7b900",
  },
},
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={BlogPostCreate.modules}
                    formats={BlogPostCreate.formats}
                    placeholder="Write your article here..."
                  />
                </Box>
              </Box>
            </Stack>
          </Paper>

          <Stack spacing={2}>
            <Paper variant="outlined" sx={panelSx}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
                Details
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth size="small" sx={{
    ...authFieldSx,
    "& .MuiInputLabel-root": {
      color: "#000",
    },
  }}>
                  <InputLabel id="post-category-label">Category *</InputLabel>
                  <Select
                    labelId="post-category-label"
                    value={category}
                    label="Category *"
                    IconComponent={KeyboardArrowDownRoundedIcon}
                    MenuProps={selectMenuProps}
                    onChange={(event) => setCategory(event.target.value)}
                  >
                    {groupedCategories.flatMap((group) => [
                      <ListSubheader key={`${group.label}-header`}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: group.color,
                            }}
                          />
                          <span>{group.label}</span>
                        </Stack>
                      </ListSubheader>,
                      ...group.items.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      )),
                    ])}
                    {ungroupedCategories.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Autocomplete
                  multiple
                  freeSolo
                  popupIcon={<KeyboardArrowDownRoundedIcon />}
                  componentsProps={{ paper: tagAutocompletePaperProps }}
                  options={allTags}
                  value={tags}
                  getOptionLabel={(option) => option.name ?? option}
                  isOptionEqualToValue={(option, value) =>
                    option.id && value.id
                      ? option.id === value.id
                      : option.name === value.name
                  }
                  onChange={(_, values) => {
                    const normalized = values
                      .map((item) =>
                        typeof item === "string"
                          ? { id: null, name: item.trim() }
                          : item,
                      )
                      .filter((item) => item.name);
                    setTags(normalized);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      size="small"
                      helperText="Select existing tags or type a new one."
                      sx={{
    ...authFieldSx,
    "& .MuiInputLabel-root": {
      color: "#000",
    },
  }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((item, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={`${item.id || "new"}-${item.name}`}
                        label={item.name}
                        size="small"
                      />
                    ))
                  }
                />
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={panelSx}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                Featured Image
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ fontSize: 12, mt: 0.5, mb: 2 }}
              >
                Optional. Maximum file size: 100 KB. The default cover will be
                used if no image is uploaded.
              </Typography>
              <Box sx={{ position: "relative", mb: 1.5 }}>
                <Box
                  component="img"
                  src={
                    localImagePreview ||
                    existingImageUrl ||
                    BLOG_FALLBACK_IMAGE
                  }
                  alt="Post cover preview"
                  sx={{
                    width: "100%",
                    height: 145,
                    objectFit: "cover",
                    display: "block",
                    borderRadius: 1.5,
                  }}
                />
                {localImagePreview && (
                  <IconButton
                    size="small"
                    aria-label="Remove featured image"
                    title="Remove featured image"
                    onClick={() => setFeaturedImage(null)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      width: 30,
                      height: 30,
                      bgcolor: "#fff",
                      color: "#dc2626",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 2px 8px rgba(15, 23, 42, 0.18)",
                      "&:hover": { bgcolor: "#fff1f2", color: "#b91c1c" },
                    }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                )}
              </Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadOutlinedIcon />}
                fullWidth
                sx={{ textTransform: "none" }}
              >
                {featuredImage ? "Change image" : "Upload file"}
                <input
                  key={
                    featuredImage
                      ? `${featuredImage.name}-${featuredImage.lastModified}`
                      : "empty-image-input"
                  }
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
              {featuredImage && (
                <Typography
                  color="text.secondary"
                  noWrap
                  sx={{ fontSize: 11, mt: 1 }}
                >
                  {featuredImage.name}
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" sx={panelSx}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
                Publish
              </Typography>
              <Stack spacing={1.25}>
                <Button
                  variant="contained"
                  startIcon={
                    loading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <PublishRoundedIcon />
                    )
                  }
                  onClick={() => handleSubmit("published")}
                  disabled={loading}
                  fullWidth
                  sx={yellowButtonSx}
                >
                  Publish Now
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </Container>

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
}

BlogPostCreate.modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
  clipboard: { matchVisual: false },
};

BlogPostCreate.formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "ordered",
  "link",
  "image",
];
